import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import '../styles/GlobalDesign.css';

const API = process.env.NODE_ENV === 'production'
  ? '/api'
  : (process.env.REACT_APP_API_URL || '/api');

const EMPTY_CONFIG = {
  nodeName: 'mediamtx-primary',
  apiUrl: '',
  hlsBaseUrl: '',
  username: '',
  password: '',
  bearerToken: '',
};

function AdminMediaMtx() {
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [paths, setPaths] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [categoryName, setCategoryName] = useState('');
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);

  const token = () => localStorage.getItem('adminToken') || localStorage.getItem('token');
  const headers = (json = false) => ({
    Authorization: `Bearer ${token()}`,
    ...(json ? { 'Content-Type': 'application/json' } : {}),
  });

  useEffect(() => {
    const authToken = localStorage.getItem('adminToken') || localStorage.getItem('token');
    fetch(`${API}/admin/mediamtx/config`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(async response => ({ response, data: await response.json() }))
      .then(({ response, data }) => {
        if (!response.ok) throw new Error(data.error || 'Configuration indisponible');
        if (data.config) setConfig(current => ({ ...current, ...data.config }));
      })
      .catch(error => setMessage({ ok: false, text: error.message }));
  }, []);

  const updateConfig = (key, value) => setConfig(current => ({ ...current, [key]: value }));

  const call = async (path, body) => {
    const response = await fetch(`${API}/admin/mediamtx/${path}`, {
      method: 'POST',
      headers: headers(true),
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Opération impossible');
    return data;
  };

  const save = async () => {
    setBusy('save'); setMessage(null);
    try {
      const response = await fetch(`${API}/admin/mediamtx/config`, {
        method: 'PUT', headers: headers(true), body: JSON.stringify(config),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Sauvegarde impossible');
      setConfig(current => ({ ...current, ...data.config, password: '', bearerToken: '' }));
      setMessage({ ok: true, text: 'Configuration MediaMTX enregistrée.' });
    } catch (error) {
      setMessage({ ok: false, text: error.message });
    } finally { setBusy(''); }
  };

  const preview = async () => {
    setBusy('preview'); setMessage(null);
    try {
      const data = await call('preview', config);
      setPaths(data.paths || []);
      setSelected(new Set((data.paths || []).map(path => path.name)));
      setMessage({ ok: true, text: `${data.total} path(s) trouvé(s) via ${data.endpoint}.` });
    } catch (error) {
      setMessage({ ok: false, text: error.message });
    } finally { setBusy(''); }
  };

  const sync = async () => {
    if (!selected.size) return setMessage({ ok: false, text: 'Sélectionnez au moins un path.' });
    setBusy('sync'); setMessage(null);
    try {
      const data = await call('sync', { config, paths: [...selected], categoryName });
      setMessage({
        ok: true,
        text: `${data.created} chaîne(s) créée(s), ${data.updated} mise(s) à jour. Les nouvelles chaînes restent désactivées.`,
      });
      await preview();
    } catch (error) {
      setMessage({ ok: false, text: error.message });
    } finally { setBusy(''); }
  };

  const allSelected = useMemo(() => paths.length > 0 && selected.size === paths.length, [paths, selected]);
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(paths.map(path => path.name)));
  const togglePath = name => setSelected(current => {
    const next = new Set(current);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  return (
    <AdminLayout>
      <div className="admin-page mediamtx-admin-page">
        <div className="admin-page-header">
          <h1 className="admin-page-title">MediaMTX</h1>
          <p className="admin-page-subtitle">Importer et synchroniser les paths live dans Fluxivo</p>
        </div>

        {message && <div className={`alert ${message.ok ? 'alert-success' : 'alert-error'}`}>{message.text}</div>}

        <div className="admin-card mediamtx-config-card">
          <h2 className="card-title">Connexion</h2>
          <div className="mediamtx-form-grid">
            <div className="form-group">
              <label className="form-label">Nom du nœud</label>
              <input className="form-input" value={config.nodeName} onChange={e => updateConfig('nodeName', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">API MediaMTX</label>
              <input className="form-input" type="url" placeholder="http://127.0.0.1:9997" value={config.apiUrl} onChange={e => updateConfig('apiUrl', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Base HLS</label>
              <input className="form-input" type="url" placeholder="http://127.0.0.1:8888" value={config.hlsBaseUrl} onChange={e => updateConfig('hlsBaseUrl', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Utilisateur API</label>
              <input className="form-input" autoComplete="off" value={config.username} onChange={e => updateConfig('username', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Mot de passe API</label>
              <input className="form-input" type="password" autoComplete="new-password" placeholder={config.hasPassword ? 'Déjà configuré' : ''} value={config.password || ''} onChange={e => updateConfig('password', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Bearer token</label>
              <input className="form-input" type="password" autoComplete="new-password" placeholder={config.hasBearerToken ? 'Déjà configuré' : 'Optionnel'} value={config.bearerToken || ''} onChange={e => updateConfig('bearerToken', e.target.value)} />
            </div>
          </div>
          <div className="mediamtx-actions">
            <button className="btn btn-secondary" onClick={preview} disabled={Boolean(busy)}>{busy === 'preview' ? 'Test...' : 'Tester et prévisualiser'}</button>
            <button className="btn btn-primary" onClick={save} disabled={Boolean(busy)}>{busy === 'save' ? 'Sauvegarde...' : 'Enregistrer'}</button>
          </div>
        </div>

        {paths.length > 0 && (
          <div className="admin-card mediamtx-paths-card">
            <div className="mediamtx-paths-head">
              <div>
                <h2 className="card-title">Paths disponibles</h2>
                <span className="mediamtx-count">{selected.size} / {paths.length} sélectionnés</span>
              </div>
              <div className="form-group mediamtx-category-field">
                <label className="form-label">Catégorie commune</label>
                <input className="form-input" placeholder="Vide = préfixe du path" value={categoryName} onChange={e => setCategoryName(e.target.value)} />
              </div>
            </div>

            <div className="mediamtx-table-wrap">
              <table className="mediamtx-table">
                <thead><tr><th><input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Tout sélectionner" /></th><th>Path</th><th>Source</th><th>Lecteurs</th><th>Fluxivo</th></tr></thead>
                <tbody>{paths.map(path => (
                  <tr key={path.name}>
                    <td><input type="checkbox" checked={selected.has(path.name)} onChange={() => togglePath(path.name)} aria-label={`Sélectionner ${path.name}`} /></td>
                    <td><strong>{path.name}</strong><small>{path.tracks?.join(', ') || 'Codec non signalé'}</small></td>
                    <td><span className={`mediamtx-state ${path.ready ? 'is-ready' : ''}`}>{path.ready ? 'Prête' : 'Inactive'}</span></td>
                    <td>{path.readers || 0}</td>
                    <td>{path.imported ? `Importée${path.active ? ' · active' : ' · désactivée'}` : 'Nouvelle'}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div className="mediamtx-actions">
              <button className="btn btn-primary" onClick={sync} disabled={Boolean(busy) || !selected.size}>{busy === 'sync' ? 'Synchronisation...' : 'Importer la sélection'}</button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminMediaMtx;
